import { useSelector } from 'react-redux';
import { selectIsWithERP } from '@reducers/membership';
import { ncFileStatuses, withERP } from '@helpers';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { useRoute } from '@react-navigation/native';
import { Perspective } from '@v2/pages/common/enums';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { ToolsDetailsScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { selectJobId, selectJobsData, selectNcFileData } from '@reducers/details';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { selectSingleBatch } from '@reducers/batch';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';

export const useDetailsLevels = () => {
  const route = useRoute();
  const isWithERP = useSelector(selectIsWithERP);
  const ncFileData = useSelector(selectNcFileData);
  const jobs = useSelector(selectJobsData);
  const jobId = useSelector(selectJobId);
  const batch = useSelector(selectSingleBatch);

  const ncFileStatus = route.params?.ncFileStatus ?? ncFileData?.data?.ncFileStatus;
  const partId = ncFileData?.data?.partId;
  const perspective = route.params?.perspective;

  const getDetailsLevels = (currentPerspective: Perspective = perspective) => {
    const jobsAmount = perspective === Perspective.BATCH ? batch?.links?.length : jobs.data?.length;

    switch (currentPerspective) {
      case Perspective.BATCH: {
        if (ncFileStatus == null) return BatchDetailsScreen;
        if (ncFileStatus === ncFileStatuses.Unknown || jobsAmount === 0) {
          return {
            ...(partId != null ? { PART: BatchDetailsScreen.PART } : {}),
            BATCH: BatchDetailsScreen.BATCH,
            NC_FILE: BatchDetailsScreen.NC_FILE,
            TOOLS: BatchDetailsScreen.TOOLS,
          };
        }

        if (ncFileStatus === ncFileStatuses.Linked && isWithERP === false) {
          return {
            PART: BatchDetailsScreen.PART,
            BATCH: BatchDetailsScreen.BATCH,
            NC_FILE: BatchDetailsScreen.NC_FILE,
            TOOLS: BatchDetailsScreen.TOOLS,
          };
        }

        return BatchDetailsScreen;
      }
      case Perspective.NC_FILE: {
        if (ncFileStatus == null) return NCFileDetailsScreen;

        if (ncFileStatus === ncFileStatuses.Unknown) {
          return { BATCH: NCFileDetailsScreen.BATCH, NC_FILE: NCFileDetailsScreen.NC_FILE, TOOLS: NCFileDetailsScreen.TOOLS };
        }

        if ((ncFileStatus === ncFileStatuses.Linked && isWithERP === false) || (jobsAmount === 0 && jobId == null)) {
          return {
            PART: NCFileDetailsScreen.PART,
            BATCH: NCFileDetailsScreen.BATCH,
            NC_FILE: NCFileDetailsScreen.NC_FILE,
            TOOLS: NCFileDetailsScreen.TOOLS,
          };
        }

        return NCFileDetailsScreen;
      }

      case Perspective.PART: {
        return {
          PART: PartDetailsScreen.PART,
          ...(withERP(isWithERP) ? { JOBS: PartDetailsScreen.JOBS } : {}),
          BATCH: PartDetailsScreen.BATCH,
          NC_FILE: PartDetailsScreen.NC_FILE,
        };
      }

      case Perspective.ORDER: {
        return {
          PART: ProductionOrderDetailsScreen.PART,
          ORDER: ProductionOrderDetailsScreen.ORDER,
          BATCH: ProductionOrderDetailsScreen.BATCH,
          NC_FILE: ProductionOrderDetailsScreen.NC_FILE,
        };
      }

      case Perspective.JOBS: {
        return {
          PART: JobDetailsScreen.PART,
          JOBS: JobDetailsScreen.JOBS,
          BATCH: JobDetailsScreen.BATCH,
          NC_FILE: JobDetailsScreen.NC_FILE,
        };
      }

      case Perspective.TOOLS: {
        return {
          NC_FILE: ToolsDetailsScreen.NC_FILE,
          TOOLS: ToolsDetailsScreen.TOOLS,
        };
      }

      default: {
        return {};
      }
    }
  };

  return { getDetailsLevels };
};
